<script setup lang="ts">
import { twJoin } from 'tailwind-merge'
import type { Button, DeepPartial } from '#ui/types'
import type { PropType } from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  class: {
    default: undefined,
    type: [
      String,
      Object,
      Array,
    ] as PropType<any>,
  },
  description: {
    default: undefined,
    type: String,
  },
  even: {
    default: false,
    type: Boolean,
  },
  links: {
    default: undefined,
    type: Array as PropType<(Button & { class?: string, click?: (...args: any[]) => void })[]>,
  },
  orientation: {
    default: 'vertical',
    type: String as PropType<'horizontal' | 'vertical'>,
  },
  reverse: {
    default: false,
    type: Boolean,
  },
  title: {
    default: undefined,
    type: String,
  },
  ui: {
    default: () => ({}),
    type: Object as PropType<DeepPartial<typeof config.value>>,
  },
})

const config = computed(() => {
  const container: string = twJoin(
    'gap-16 sm:gap-y-12',
    props.orientation === 'vertical' && 'flex flex-col',
    props.orientation === 'horizontal' && 'flex flex-col lg:items-center',
    props.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row',
  )

  let base: string = props.orientation === 'vertical' ? 'text-center w-full' : 'w-full'

  if (props.even) {
    base = 'w-full lg:w-1/2'
  }
  else {
    base = 'w-full lg:w-[40%]'
  }

  if (props.reverse && props.orientation === 'horizontal') {
    base = twJoin(base, 'lg:text-right')
  }

  const links: string = twJoin(
    'mt-10 flex flex-wrap gap-x-6 gap-y-3',
    props.orientation === 'vertical' && 'justify-center',
    props.reverse && props.orientation === 'horizontal' && 'lg:justify-end',
  )

  return {
    base,
    container,
    description: 'mt-4 text-lg md:text-xl tracking-tight text-gray-600 dark:text-gray-300 text-balance',
    headline: 'mb-10',
    links,
    title: 'text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance',
    wrapper: 'relative w-full',
  }
})

const { attrs, ui } = useUI('landing.hero', toRef(props, 'ui'), config, toRef(props, 'class'), true)
</script>

<template>
  <div
    :class="ui.wrapper"
    v-bind="attrs"
  >
    <slot name="top" />

    <div :class="ui.container">
      <div :class="ui.base">
        <div
          v-if="$slots.headline"
          :class="ui.headline"
        >
          <slot name="headline" />
        </div>

        <h1 :class="ui.title">
          <slot name="title">
            {{ title }}
          </slot>
        </h1>

        <div
          v-if="description || $slots.description || $slots.secondaryDescription"
          :class="ui.description"
        >
          <p>
            <slot name="description">
              {{ description }}
            </slot>
          </p>

          <slot name="secondaryDescription" />
        </div>

        <div
          v-if="links?.length || $slots.links"
          :class="ui.links"
        >
          <slot name="links">
            <UButton
              v-for="(link, index) in links"
              :key="index"
              v-bind="link"
              @click="link.click"
            />
          </slot>
        </div>
      </div>

      <div :class="even ? 'w-full lg:w-1/2' : 'w-full lg:w-[60%]'">
        <slot />
      </div>
    </div>

    <slot name="bottom" />
  </div>
</template>
